import { Button } from "primereact/button";
import { useEffect, useState, useRef } from "react";
import { InputText } from "primereact/inputtext";
import { Toast, ToastMessage } from "primereact/toast";
import { Dialog } from "primereact/dialog";
import { Checkbox } from "primereact/checkbox";
import { useTranslation } from "react-i18next";
import { POSModel } from "../../models/orders/order-list-model";
import orderServices from "../../services/orders/orderservice";
import "../../assests/css/common/responsive-ui.css";
import AddProductStockDialog from "./AddProductStockDialog";
import posService from "../../services/pos/pos.service";
import { Badge } from "primereact/badge";
import IncomingRequestsDialog from "./IncomingRequestsDialog";
import RequestStatusDialog from "./RequestStatusDialog";

interface SearchPanelProps {
  onSearch: (query: string) => void;
  setLoading: (loading: boolean) => void;
  onPOSChange: (posId: any) => void;
  onAddSuccess: (posId: number[]) => void;
  onStatusFilterChange?: (statuses: string[]) => void;
}

const PosStockSummarySearch: React.FC<SearchPanelProps> = ({
  onSearch,
  setLoading,
  onPOSChange,
  onAddSuccess,
  onStatusFilterChange,
}) => {
  const { t } = useTranslation();
  const [keywordsearch, setKeywordsearch] = useState<string>("");
  const [posData, setPOSData] = useState<POSModel[]>([]);
  const [showAddProductModal, setShowAddProductModal] = useState(false);
  const [isPOSSelected, setIsPOSSelected] = useState<number[]>([]);
  const toast = useRef<Toast>(null);
  const statusOptions = ["Low", "Sufficient", "Critical"];
  const [selectedStatuses, setSelectedStatuses] = useState<string[]>([]);
  const [showRequestsDialog, setShowRequestsDialog] = useState(false);
  const [totalRequestCount, setTotalRequestCount] = useState(0);
  const [showRequestStatusDialog, setShowRequestStatusDialog] = useState(false);

  const validatePOS = (): boolean => {
    const isValid = !!isPOSSelected && isPOSSelected.length > 0;
    return isValid;
  };

  const handleSearch = () => {
    if (!validatePOS()) {
      showMessage("Error", "Please select a POS", "warn");
      return;
    }
    setLoading(true);
    onSearch(keywordsearch);
    onStatusFilterChange?.(selectedStatuses);
  };

  const resetSearchFields = () => {
    setKeywordsearch("");
    onSearch("");
    posData.length > 1 && setIsPOSSelected([]);
    setSelectedStatuses([]);
  };

  const showMessage = (
    label: string,
    message: string,
    severity: ToastMessage["severity"]
  ) => {
    toast.current?.show({
      severity: severity,
      summary: label,
      detail: message,
      life: 3000,
    });
  };

  const getALLPOS = async () => {
    try {
      const response: POSModel[] = await orderServices.getPOS();
      setPOSData(response);
      // Auto-select if there's only one POS
      if (response.length === 1) {
        const singlePOS = [response[0].posID];
        setIsPOSSelected(singlePOS);
        onPOSChange(singlePOS);
      }
    } catch (error) {
      showMessage("Error", "Failed to fetch POS data", "error");
    }
  };

  useEffect(() => {
    getALLPOS();
  }, []);

  const fetchRequestCounts = async () => {
  if (!isPOSSelected || isPOSSelected.length === 0) return;

  try {
    const [outwardRes, inwardRes] = await Promise.all([
      posService.Getposrequest(isPOSSelected),
      posService.GetposInwardrequest(isPOSSelected)
    ]);

    const outwardCount = Array.isArray(outwardRes) ? outwardRes.length : 0;
    const inwardCount = Array.isArray(inwardRes) ? inwardRes.length : 0;
    
    setTotalRequestCount(outwardCount + inwardCount);
  } catch (error) {
    console.error("Error fetching request counts:", error);
  }
};

  useEffect(() => {
    const notifyPOSChange = async () => {
      if (Array.isArray(isPOSSelected) && isPOSSelected.length > 0) {
        onPOSChange(isPOSSelected);
        fetchRequestCounts();
      } else {
        onPOSChange([]);
        setTotalRequestCount(0);
      }
    };
    notifyPOSChange();
  }, [JSON.stringify(isPOSSelected)]);

  const handleOnSuccess = () => {
    setShowAddProductModal(false);
    showMessage("Add Product", "Product stock config added successfully", "success");
    onAddSuccess(isPOSSelected);
  };

  useEffect(() => {
    if (typeof onStatusFilterChange === "function") {
      onStatusFilterChange(selectedStatuses);
    }
  }, [JSON.stringify(selectedStatuses)]);

  return (
    <div className="col-12" style={{ width: "100%", padding: "0px", marginBottom: "0.5rem" }}>
      <Toast ref={toast} />
      <div className="card border-noround p-2" style={{ minHeight: "3.5rem" }}>
        <div className="grid align-items-center">
          {/* POS Dropdown */}
          <div className="col-12 md:col-5">
            <div className="flex flex-wrap gap-3">
              {(posData || []).map((pos: any) => (
                <div key={pos.posID} className="flex align-items-center gap-2">
                  <Checkbox
                    inputId={`pos-${pos.posID}`}
                    value={pos.posID}
                    disabled={posData.length === 1}
                    onChange={(e) => {
                      const val = e.value;
                      setIsPOSSelected((prev: any) =>
                        prev.includes(val)
                          ? prev.filter((p: any) => p !== val)
                          : [...prev, val]
                      );
                    }}
                    checked={isPOSSelected.includes(pos.posID)}
                  />
                  <label htmlFor={`pos-${pos.posID}`}>{pos.posCode}</label>
                </div>
              ))}
            </div>

            {/* {isPOSSelected.length === 0 && (
              <small className="p-error">POS is required</small>
            )} */}
          </div>

          {/* Search Input */}
          <div className="col-12 md:col-2">
            <div className="p-input-icon-left w-full">
              <i className="pi pi-search" />
              <InputText
                type="search"
                value={keywordsearch}
                onChange={(e) => {
                  const value = e.target.value;
                  setKeywordsearch(value);
                  onSearch(value);
                }}
                placeholder={t("Product search")}
                className="w-full"
                style={{
                  border: "1px solid #75bb8aff",
                  boxShadow: "0 0 0 1px #75bb8aff",
                }}
              />
            </div>
          </div>

          {/* Reset Button */}
          <div className="col-6 md:col-1">
            <Button
              label="Reset"
              onClick={resetSearchFields}
              severity="secondary"
              className="w-full"
              text
              raised
            />
          </div>

          {/* Add Product & Request Status Buttons */}
          <div className="col-12 md:col-4 ml-auto flex justify-content-end gap-2 mt-2 md:mt-0">
            <Button
              label="Add Product"
              className="bg-blue-700 text-white border-round"
              onClick={() => setShowAddProductModal(true)}
              text
              raised
              disabled={isPOSSelected.length === 0}
              tooltip={
                isPOSSelected.length === 0
                  ? "Please select at least one POS to add products."
                  : "Add new product stock config to the selected POS."
              }
              tooltipOptions={{ position: "left" }}
            />

            <Button
              label="Request Status"
              className="bg-orange-600 text-white border-round"
              onClick={() => setShowRequestStatusDialog(true)}
              text
              raised
              disabled={isPOSSelected.length === 0}
              tooltip={
                isPOSSelected.length === 0
                  ? "Please select at least one POS to view request status."
                  : "View all request statuses for the selected POS."
              }
              tooltipOptions={{ position: "left" }}
            />

            <div style={{ position: "relative", display: "inline-block" }}>
              <Button
                type="button"
                icon="pi pi-bell"
                severity="secondary"
                disabled={isPOSSelected.length === 0}
                onClick={() => totalRequestCount > 0 && setShowRequestsDialog(true)}
                tooltip={isPOSSelected.length === 0 ? "Select a POS first" : "View incoming requests"}
                tooltipOptions={{ position: "left" }}
              />
              {totalRequestCount > 0 && (
                <Badge
                  value={totalRequestCount}
                  severity="danger"
                  style={{
                    position: "absolute",
                    top: "-8px",
                    right: "-8px"
                  }}
                />
              )}
            </div>
          </div>

          {/* Status Filters */}
          <div className="col-12 mt-2">
            <div className="flex flex-wrap gap-3 mt-2">
              {statusOptions.map((status) => (
                <div key={status} className="flex align-items-center gap-2">
                  <Checkbox
                    inputId={`status-${status}`}
                    checked={selectedStatuses.includes(status)}
                    onChange={() =>
                      setSelectedStatuses((prev) =>
                        prev.includes(status)
                          ? prev.filter((s) => s !== status)
                          : [...prev, status]
                      )
                    }
                  />
                  <label htmlFor={`status-${status}`} className="m-0" style={{ marginLeft: 6 }}>
                    {status}
                  </label>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Add Product Dialog */}
        <Dialog
          visible={showAddProductModal}
          className="quickview w-30"
          breakpoints={{ "960px": "90vw", "641px": "95vw" }}
          modal
          style={{ width: "35rem" }}
          onHide={() => setShowAddProductModal(false)}
        >
          <AddProductStockDialog
            posIds={isPOSSelected}
            onSuccess={handleOnSuccess}
          />
        </Dialog>

        <IncomingRequestsDialog
          visible={showRequestsDialog}
          onHide={() => setShowRequestsDialog(false)}
          selectedPosIds={isPOSSelected}
          onDataRefresh={() => {
            // Refresh the main product stock data after requests are accepted/rejected
            onAddSuccess(isPOSSelected);
          }}
        />

        <RequestStatusDialog
          visible={showRequestStatusDialog}
          onHide={() => setShowRequestStatusDialog(false)}
          selectedPosIds={isPOSSelected}
        />
      </div>
    </div>
  );
};

export default PosStockSummarySearch;
